import { useStytchMemberSession } from '@stytch/react/b2b';
import { Navigate, useLocation } from 'react-router-dom';
import { useQuery, useMutation, queryOptions } from '@tanstack/react-query';
import { User } from 'types/api';
import { datadogRum } from '@datadog/browser-rum';

import { api } from './api-client';
import { QueryConfig } from './react-query';

// api call definitions for auth (types, schemas, requests):
// these are not part of features as this is a module shared across features

type GetUserResponse = {
    user: User;
};

const getUser = async (): Promise<User> => {
    const response = await api.get<GetUserResponse, GetUserResponse>(
        '/app/api/v1/user',
    );
    return response.user;
};

export const getUserQueryOptions = () =>
    queryOptions({
        queryKey: ['user'],
        queryFn: getUser,
    });

const logout = (): Promise<void> => {
    return api.post('/app/api/v1/logout');
};

const authenticate = async (): Promise<void> => {
    await api.post('/public/api/v1/auth');
};

type UseUserOptions = {
    queryConfig?: QueryConfig<typeof getUserQueryOptions>;
};

export const useUser = ({ queryConfig }: UseUserOptions = {}) => {
    const { data, isLoading } = useQuery({
        ...getUserQueryOptions(),
        ...queryConfig,
    });

    if (data) {
        datadogRum.setUser({
            id: data.id,
            email: data.email,
            name: data.name,
        });
    }

    return { data, isLoading };
};

export const useAuthenticate = (
    onSuccess: () => void,
    onError: (error: Error) => void,
) => {
    const mutation = useMutation({
        onSuccess: onSuccess,
        onError: onError,
        mutationFn: authenticate,
    });
    return mutation;
};

export const useLogout = (onSuccess: () => void) => {
    const mutation = useMutation({
        mutationFn: logout,
        onSuccess: onSuccess,
    });
    return mutation;
};

export const ProtectedRoute = ({ children }: { children: React.ReactNode }) => {
    const location = useLocation();
    const { session } = useStytchMemberSession();

    if (!session) {
        return (
            <Navigate
                to={`/authenticate?redirectTo=${encodeURIComponent(location.pathname)}`}
                replace
            />
        );
    }

    return children;
};
